import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@helpers/translate";
import axios from "@helpers/axios/private.axios";

import CardIcon from "@assets/icons/CardIcon";
import CruptoCurrencyIcon from "@assets/icons/CruptoCurrencyIcon";
import PopularIcon from "@assets/icons/PopularIcon";
import VisaMastercardIcon from "@assets/icons/methods/VisaMastercardIcon";
import TetherTRCIcon from "@assets/icons/methods/TetherTRCIcon";
import TetherBEPIcon from "@assets/icons/methods/TetherBEPIcon";
import TetherERCIcon from "@assets/icons/methods/TetherERCIcon";
import EthereumERCIcon from "@assets/icons/methods/EthereumERCIcon";
import USDCoinBEPIcon from "@assets/icons/methods/USDCoinBEPIcon";
import USDCoinERCIcon from "@assets/icons/methods/USDCoinERCIcon";
import USDCoinBaseIcon from "@assets/icons/methods/USDCoinBaseIcon";
import BitcoinIcon from "@assets/icons/methods/BitcoinIcon";
import TronIcon from "@assets/icons/methods/TronIcon";
import LitecoinIcon from "@assets/icons/methods/LitecoinIcon";

import "./index.sass";

const paymentMethodLogos = {
  visa_uah: VisaMastercardIcon,
  visa_usd: VisaMastercardIcon,

  usdt_trc20: TetherTRCIcon,
  usdt_bep20: TetherBEPIcon,
  usdt_erc20: TetherERCIcon,
  eth_erc20: EthereumERCIcon,
  usdc_bep20: USDCoinBEPIcon,
  usdc_erc20: USDCoinERCIcon,
  usdc_base: USDCoinBaseIcon,
  btc: BitcoinIcon,
  ton: "/images/Toncoin.png",
  trx: TronIcon,
  ltc: LitecoinIcon,
  eth_base: EthereumERCIcon,
};

const getId = (method, index) =>
  String(method?.method_key ?? method?.slug ?? method?.id ?? index);

const getMethodLogo = (method) => {
  return (
    paymentMethodLogos[method?.method_key] ||
    paymentMethodLogos[method?.slug] ||
    method?.logo
  );
};

const normalizeGateway = (gateway) => ({
  ...gateway,
  endpoint: `/gateway/${gateway.id}/deposit`,
});

const MethodIcon = ({ method }) => {
  const Icon = getMethodLogo(method);

  if (!Icon) return null;

  if (typeof Icon === "string") {
    return (
      <span className="payment-methods__icon-wrap">
        <img
          src={Icon}
          alt={method.title}
          className="payment-methods__icon"
        />
      </span>
    );
  }

  return (
    <span className="payment-methods__svg-icon-wrap">
      <Icon className="payment-methods__svg-icon" />
    </span>
  );
};

const PaymentMethods = ({ searchQuery = "", onSelect }) => {
  const { __ } = useTranslation();

  const [fiatMethods, setFiatMethods] = useState([]);
  const [cryptoMethods, setCryptoMethods] = useState([]);

  const getGateways = async () => {
    try {
      const [fiatResponse, cryptoResponse] = await Promise.all([
        axios.get("/gateway/fiat"),
        axios.get("/gateway/crypto"),
      ]);

      setFiatMethods(
        (fiatResponse?.data?.data || []).map(normalizeGateway),
      );

      setCryptoMethods(
        (cryptoResponse?.data?.data || []).map(normalizeGateway),
      );
    } catch (error) {
      console.log("Gateways loading error:", error);
    }
  };

  useEffect(() => {
    getGateways();
  }, []);

  const popularMethodKeys = [
    "visa_uah",
    "usdt_trc20",
    "btc",
    "usdc_bep20",
  ];

  const search = searchQuery.trim().toLowerCase();

  const filterMethods = (methods) => {
    if (!search) return methods;

    return methods.filter((method) => {
      const title = method.title?.toLowerCase();
      const slug = method.slug?.toLowerCase();
      const type = method.type?.toLowerCase();
      const network = method.network?.toLowerCase();
      const asset = method.asset?.toLowerCase();
      const methodKey = method.method_key?.toLowerCase();

      return (
        title?.includes(search) ||
        slug?.includes(search) ||
        type?.includes(search) ||
        network?.includes(search) ||
        asset?.includes(search) ||
        methodKey?.includes(search)
      );
    });
  };

  const popularMethods = useMemo(() => {
    const methods = [...fiatMethods, ...cryptoMethods].filter((method) =>
      popularMethodKeys.includes(method.method_key),
    );

    return filterMethods(methods);
  }, [fiatMethods, cryptoMethods, search]);

  const filteredFiatMethods = useMemo(() => {
    return filterMethods(fiatMethods);
  }, [fiatMethods, search]);

  const filteredCryptoMethods = useMemo(() => {
    return filterMethods(cryptoMethods);
  }, [cryptoMethods, search]);

  const sections = [
    {
      key: "popular",
      title: __("wallet.deposit.popular"),
      Icon: PopularIcon,
      methods: popularMethods,
    },
    {
      key: "cards",
      title: __("wallet.deposit.cards"),
      Icon: CardIcon,
      methods: filteredFiatMethods,
    },
    {
      key: "crypto",
      title: __("common.cryptocurrency"),
      Icon: CruptoCurrencyIcon,
      methods: filteredCryptoMethods,
    },
  ];

  const hasMethods = sections.some((section) => section.methods.length);

  const handleSelect = (method, sectionKey) => {
    const logo = getMethodLogo(method);

    const processingTime =
      method.processing_time_label === "instant"
        ? __("wallet.deposit.instant")
        : method.processing_time_label;

    onSelect?.({
      id: method.id,
      slug: method.slug,
      method_key: method.method_key,
      title: method.title,
      type: method.type,
      kind: method.type,
      endpoint: method.endpoint,
      icon: logo,
      logo,
      asset: method.asset,
      network: method.network,
      commission: method.comission ?? method.commission ?? null,
      minDepositAmount: method.deposit_min ?? null,
      processingTime,
      processingTimeMinutes: method.processing_time_minutes,
      method,
      sectionKey,
    });
  };

  // Тимчасово вимикаємо методи Visa/Mastercard,
  // оскільки їхній флоу ще не реалізований.
  const isMethodDisabled = (method) => {
    return (
      method.method_key === "visa_uah" ||
      method.method_key === "visa_usd"
    );
  };

  return (
    <div className="payment-methods">
      {!hasMethods && (
        <p className="payment-methods__empty">
          {__("wallet.deposit.no_payment_methods_found")}
        </p>
      )}

      {sections.map((section) => {
        if (!section.methods.length) return null;

        const SectionIcon = section.Icon;

        return (
          <div key={section.key} className="payment-methods__section">
            <div className="payment-methods__title">
              <SectionIcon />

              <h3>{section.title}</h3>
            </div>

            <ul className="payment-methods__list">
              {section.methods.map((method, index) => {
                const id = getId(method, index);
                const methodKey = `${section.key}-${id}`;
                const disabled = isMethodDisabled(method);

                const processingTime =
                  method.processing_time_label === "instant"
                    ? __("wallet.deposit.instant")
                    : method.processing_time_label;

                return (
                  <li
                    key={methodKey}
                    className={`payment-methods__item ${
                      disabled ? "disabled" : ""
                    }`}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    onClick={() => {
                      if (disabled) return;

                      handleSelect(method, section.key);
                    }}
                  >
                    <div className="payment-methods__item-top">
                      <MethodIcon method={method} />

                      <span className="payment-methods__name">
                        {method.title}
                      </span>
                    </div>

                    <div className="payment-methods__item-bottom">
                      <span className="payment-methods__min">
                        <p>
                          {__("wallet.deposit.min")}: ${method.deposit_min}
                        </p>
                      </span>

                      <span className="payment-methods__time">
                        <p>{processingTime || "—"}</p>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

export default PaymentMethods;
